import { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import {
  Typography,
  Stack,
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import useConfirmation from '@/hooks/useConfirmation';
import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid';
import { useFormik } from 'formik';
import { OrganizationContext } from './OrganizationLayout';
import { Invitation, InvitationSchema } from '@/types';
import useOrganizations from '../useOrganizations';
import { useAccessControl } from '@/features/access-control';
import { DataGrid } from '@/components';
import { deleteInvitation } from '../slice';

const Invitations = () => {
  const { organizationId } = useParams();
  const { hasAccess } = useAccessControl();
  const { currentOrganization } = useOutletContext<OrganizationContext>();

  const {
    addAccessInvitation,
    fetchAccessInvitations,
  } = useOrganizations();

  const [renderConfirmationDialog, confirmTableDeletion] = useConfirmation({
    title: 'Eliminar',
    message: `Desea eliminar la invitación?`,
    dispatcher: deleteInvitation,
  });

  const columns: GridColDef[] = useMemo(
    () => [
      {
        flex: 2,
        field: 'email',
        headerName: 'User',
        hideSortIcons: true,
        disableColumnMenu: true,
      },
      {
        flex: 1,
        field: 'role',
        headerName: 'Rol',
        hideSortIcons: true,
        disableColumnMenu: true,
      },
      {
        flex: 2,
        field: 'status',
        headerName: 'Estado',
        hideSortIcons: true,
        disableColumnMenu: true,
      },
      {
        type: 'actions',
        field: 'actions',
        headerName: 'Acciones',
        align: 'right',
        headerAlign: 'right',
        hideSortIcons: true,
        disableColumnMenu: true,
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            onClick={() => confirmTableDeletion({
              organizationId,
              invitationId: params.row.id,
            })}
            label="Eliminar"
          />,
        ],
      },
    ],
    []
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      id: null,
      email: '',
      role: 'admin',
      status: 'pending',
    },
    onSubmit: async (values: Invitation) => {
      try {
        const safeValues = InvitationSchema.parse(values);
        await addAccessInvitation(safeValues);
        setIsDialogOpen(false);
      } catch (error) {
        console.error(error);
      }
    },
  });

  useEffect(() => {
    fetchAccessInvitations();
  }, []);

  return (
    <Stack spacing={3}>
      <Stack spacing={2} direction="row" alignItems="center">
        <Typography variant="h2" flex={1}>
          Invitaciones
        </Typography>
        {hasAccess('create.invitation') && (
          <Button
            size="large"
            color="success"
            fullWidth={false}
            onClick={() => setIsDialogOpen(true)}
          >
            Compartir Acceso
          </Button>
        )}
      </Stack>

      <DataGrid
        rows={currentOrganization?.invitations ?? []}
        columns={columns}
      />

      <Dialog
        open={isDialogOpen}
        keepMounted
        onClose={() => setIsDialogOpen(false)}
        aria-describedby="alert-dialog-slide-description"
        maxWidth="xl"
      >
        <DialogTitle>Crear Acceso</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              width: 600,
            }}
          >
            <Stack spacing={2}>
              <TextField
                name="email"
                label="Email"
                variant="outlined"
                value={formik.values.email}
                onChange={formik.handleChange}
              />
              <FormControl>
                <FormLabel>Rol</FormLabel>
                <RadioGroup
                  row
                  name="role"
                  value={formik.values.role}
                  onChange={formik.handleChange}
                >
                  <FormControlLabel
                    value="admin"
                    control={<Radio />}
                    label="Admin"
                  />
                  <FormControlLabel
                    value="waiter"
                    control={<Radio />}
                    label="Waiter"
                  />
                </RadioGroup>
              </FormControl>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: 3, paddingTop: 0 }}>
          <Button onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
          <Button onClick={formik.handleSubmit}>Invitar</Button>
        </DialogActions>
      </Dialog>
      {renderConfirmationDialog()}
    </Stack>
  );
};

export default Invitations;
